import { POWER_SCORE_LENS_LABEL_IDS, type ScoreSource } from "@/lib/score-source";

// The single inventory of every spark navigation surface (sidebar pillars,
// their submenu pages, topbar icons) mapped to a genesis 242-access-model
// feature key, "active" or "planned". "Admin" is deliberately excluded: it
// gates on Permissions, not a feature key (see AppShell.tsx).
//
// WHY THIS EXISTS: feature-catalog.test.ts cross-checks this against the
// real PILLARS structure and the topbar's data-assistant-anchor markers, so
// a new sidebar entry or topbar icon with no catalog entry fails the build,
// the same discipline genesis's own `Feature` enum +
// `seededCatalogMatchesTheEnum()` test applies to the server-side catalog
// (genesis-service/src/main/java/com/smycles/genesis/access/Feature.java).
//
// STATUS IS NOT A TOGGLE. "active" means genesis's `feature` table already
// has this row and spark enforces it (RequireFeature / useHasFeature /
// skip). "planned" means the surface SHOULD get its own key eventually, but
// no such row exists yet - these entries are pure inventory, read by
// nothing at runtime. Wiring a "planned" key into RequireFeature before
// genesis seeds it would make that page vanish for every principal, because
// reachableFeatures never returns a key nobody granted (fail-closed by
// construction, see access-features.tsx). Flipping a "planned" entry to
// "active" is therefore always a two-step change: genesis ships the key
// first, spark wires the guard second - never the other way round.
export type FeatureCatalogStatus = "active" | "planned";

// Only meaningful when status is "active" - see genesis's Feature.java class
// javadoc for the authoritative version of this split:
//   "server"  - genesis also guards the underlying resolver(s). Hiding the
//               nav here is defense-in-depth, not the only line of defense.
//   "ui-only" - genesis has NO validateFeatureReachable call for this key,
//               deliberately: the underlying data is self-scope and shared
//               with other, un-gated pages (Grow's dashboard, self-assessment
//               results, global search, match candidate picking), so a real
//               guard would gate those too. This key exists solely so
//               reachableFeatures can report it and spark can hide the
//               route - the data itself stays reachable everywhere else.
export type FeatureCatalogEnforcement = "server" | "ui-only";

export interface FeatureCatalogEntry {
  /** The genesis `feature.feature_key` this names, real or proposed. */
  key: string;
  /** Where this sits in spark's own chrome, for humans reading the catalog. */
  area: string;
  status: FeatureCatalogStatus;
  enforcement?: FeatureCatalogEnforcement;
  /**
   * Human label for the access-config matrix, for a feature that is NOT a
   * sidebar entry (a control inside a page). Nav entries need none: the
   * matrix takes their label from AppShell's PILLARS, so menu and matrix
   * never call the same page two different things.
   */
  label?: { id: string; defaultMessage: string };
}

export const FEATURE_CATALOG = {
  // --- Active, server-enforced: genesis guards the resolver itself. ---
  grow: { key: "grow", area: "GROW pillar", status: "active", enforcement: "server" },
  search: { key: "search", area: "Topbar, global search", status: "active", enforcement: "server" },
  match: { key: "match", area: "MATCH pillar", status: "active", enforcement: "server" },

  // --- Active, UI-only: no genesis guard exists or should exist for these -
  // see FeatureCatalogEnforcement above. Hiding the route is the whole point. ---
  growMap: { key: "grow.map", area: "GROW > Map", status: "active", enforcement: "ui-only" },
  saveCommunity: {
    key: "save.community",
    area: "SAVE > Community",
    status: "active",
    enforcement: "ui-only",
  },
  share: { key: "share", area: "SHARE pillar", status: "active", enforcement: "ui-only" },
  growWatch: { key: "grow.watch", area: "GROW > Watch", status: "active", enforcement: "ui-only" },
  growPlay: { key: "grow.play", area: "GROW > Play", status: "active", enforcement: "ui-only" },
  saveProfile: {
    key: "save.profile",
    area: "SAVE > Profil",
    status: "active",
    enforcement: "ui-only",
  },
  saveDocuments: {
    key: "save.documents",
    area: "SAVE > Dokumente",
    status: "active",
    enforcement: "ui-only",
  },
  savePins: { key: "save.pins", area: "SAVE > Pins", status: "active", enforcement: "ui-only" },
  save: { key: "save", area: "SAVE pillar", status: "active", enforcement: "ui-only" },
  assistant: {
    key: "assistant",
    area: "Topbar > KI-Assistent",
    status: "active",
    enforcement: "ui-only",
  },
  chat: { key: "chat", area: "Topbar > Chat", status: "active", enforcement: "ui-only" },

  // --- Active, UI-only, and NOT a navigation surface: a control inside a page. ---
  // One lens each of a person's Powers box (own profile and /p/:id): the box offers exactly the lenses
  // held, and with none it shows the combined score alone (lib/score-source allowedScoreSources). Keyed
  // under save.profile, where the control lives, so the access overview nests them beneath the profile
  // page; their parent save.profile.powers-source is only the heading there and is no longer read here.
  // Genesis seeds them (V0128) for every Role that held the parent, which it retires once this reader
  // is deployed. Labels are the lens words, so overview and box say the same thing.
  saveProfilePowersSourceSelf: {
    key: "save.profile.powers-source.self",
    area: "SAVE > Profil, Powers-Box, Linse Selbstbewertung",
    status: "active",
    enforcement: "ui-only",
    label: { id: POWER_SCORE_LENS_LABEL_IDS.SELF_ASSESSMENT, defaultMessage: "Self-assessment" },
  },
  saveProfilePowersSourceSmart: {
    key: "save.profile.powers-source.smart",
    area: "SAVE > Profil, Powers-Box, Linse Smart Analyse",
    status: "active",
    enforcement: "ui-only",
    label: { id: POWER_SCORE_LENS_LABEL_IDS.SMART_ANALYSIS, defaultMessage: "Smart analysis" },
  },
  saveProfilePowersSourceCombined: {
    key: "save.profile.powers-source.combined",
    area: "SAVE > Profil, Powers-Box, Linse Kombiniert",
    status: "active",
    enforcement: "ui-only",
    label: { id: POWER_SCORE_LENS_LABEL_IDS.COMBINED, defaultMessage: "Combined" },
  },

  // --- Planned: inventory only, see the module comment above. ---
  notifications: { key: "notifications", area: "Topbar > Benachrichtigungen", status: "planned" },
} as const satisfies Record<string, FeatureCatalogEntry>;

/** The feature key behind each lens of the Powers box. */
export const POWERS_SOURCE_FEATURE_KEYS: Record<ScoreSource, string> = {
  self: FEATURE_CATALOG.saveProfilePowersSourceSelf.key,
  smart: FEATURE_CATALOG.saveProfilePowersSourceSmart.key,
  combined: FEATURE_CATALOG.saveProfilePowersSourceCombined.key,
};

/**
 * `data-assistant-anchor="topbar.…"` suffixes that name an actual feature
 * surface, mapped to their catalog entry. "topbar.help" and "topbar.user"
 * are deliberately absent: the guided-tour trigger and the account menu are
 * chrome, not features a deployment would ever want to hide per audience.
 */
export const TOPBAR_FEATURE_ANCHORS: Record<string, FeatureCatalogEntry> = {
  search: FEATURE_CATALOG.search,
  assistant: FEATURE_CATALOG.assistant,
  chat: FEATURE_CATALOG.chat,
  notifications: FEATURE_CATALOG.notifications,
};
